import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { db } from "../firebase/config";
import { useAuth } from "./AuthContext";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  updateDoc,
  addDoc,
  setDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { format, startOfDay, isToday } from "date-fns";

export const DashboardContext = createContext();
export const useDashboard = () => useContext(DashboardContext);

export function DashboardProvider({ children }) {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  // GLOBAL STATE
  const [tasks, setTasks] = useState([]); // combined tasks for dashboard
  const [habits, setHabits] = useState([]); // daily habits
  const [reminders, setReminders] = useState([]); // reminders

  // INTERNAL REFS
  const tasksRefMap = useRef({});

  // Function to update daily progress
  const updateDailyProgress = useCallback(async () => {
    if (!userId) return;

    const todayKey = format(new Date(), "yyyy-MM-dd");
    const todayStart = startOfDay(new Date());

    // Get all tasks for today
    const todayTasks = tasks.filter((task) => {
      // Include tasks that are due today or are sticky (always active)
      if (task.sticky) return true;
      if (!task.date) return true; // tasks without specific date are considered daily

      const taskDate = task.date?.toDate
        ? task.date.toDate()
        : new Date(task.date);
      return isToday(taskDate);
    });

    // Get all habits for today
    const todayHabits = habits.filter((habit) => {
      const habitStartDate = habit.startDate?.toDate
        ? habit.startDate.toDate()
        : new Date(0);
      if (isToday(habitStartDate) || habitStartDate < todayStart) {
        const type =
          habit.recurrenceType ||
          (habit.days && habit.days.length ? "SPECIFIC_DAYS" : "DAILY");
        const todayName = format(new Date(), "EEEE");

        if (type === "FLEXIBLE_WEEKLY") return true;
        if (type === "DAILY") return true;
        if (
          type === "SPECIFIC_DAYS" &&
          habit.days &&
          habit.days.includes(todayName)
        )
          return true;
      }
      return false;
    });

    // Calculate task progress
    const completedTasks = todayTasks.filter(
      (task) => task.completedDates?.[todayKey]
    ).length;
    const taskPercentage =
      todayTasks.length > 0
        ? Math.round((completedTasks / todayTasks.length) * 100)
        : 0;

    // Calculate habit progress (similar to HabitTracker logic)
    const completedHabits = todayHabits.reduce((sum, habit) => {
      const type =
        habit.recurrenceType ||
        (habit.days && habit.days.length ? "SPECIFIC_DAYS" : "DAILY");

      if (habit.completedDates?.[todayKey]) {
        return sum + 1;
      }

      // For flexible weekly habits, check if weekly target is met
      if (type === "FLEXIBLE_WEEKLY") {
        const weekKey = format(startOfDay(new Date()), "yyyy-MM-dd");
        const currentCount = habit.weeklyLogs?.[weekKey] || 0;
        const target = habit.targetCount || 1;
        if (currentCount >= target) return sum + 1;
      }

      return sum;
    }, 0);

    const habitPercentage =
      todayHabits.length > 0
        ? Math.round((completedHabits / todayHabits.length) * 100)
        : 0;

    // Update dashboard progress in Firestore - Use todayKey as document ID
    const dashboardProgressRef = doc(
      db,
      "users",
      userId,
      "dashboardProgress",
      todayKey
    );
    try {
      await setDoc(
        dashboardProgressRef,
        {
          date: todayStart,
          percentage: taskPercentage,
          updatedAt: new Date(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error updating dashboard progress:", error);
    }

    // Update habit progress in Firestore - Use todayKey as document ID
    const habitProgressRef = doc(
      db,
      "users",
      userId,
      "habitProgress",
      todayKey
    );
    try {
      await setDoc(
        habitProgressRef,
        {
          date: todayStart,
          percentage: habitPercentage,
          updatedAt: new Date(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error updating habit progress:", error);
    }
  }, [userId, tasks, habits]);

  // Migrate existing data to use date-based document IDs
  const migrateProgressData = useCallback(async () => {
    if (!userId) return;

    try {
      // Check if migration is needed
      const hasMigrated = localStorage.getItem("progressDataMigrated");
      if (hasMigrated) return;

      console.log("Starting data migration...");

      // Get all dashboard progress documents
      const progressSnapshot = await getDocs(
        collection(db, "users", userId, "dashboardProgress")
      );

      const batchPromises = [];

      progressSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.date) {
          const dateKey = format(data.date.toDate(), "yyyy-MM-dd");

          // If document ID is not the date key, we need to migrate it
          if (docSnap.id !== dateKey) {
            // Create new document with dateKey as ID
            const newRef = doc(
              db,
              "users",
              userId,
              "dashboardProgress",
              dateKey
            );
            batchPromises.push(setDoc(newRef, data, { merge: true }));

            // Delete old document
            batchPromises.push(deleteDoc(docSnap.ref));
          }
        }
      });

      // Do the same for habitProgress
      const habitSnapshot = await getDocs(
        collection(db, "users", userId, "habitProgress")
      );

      habitSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.date) {
          const dateKey = format(data.date.toDate(), "yyyy-MM-dd");

          if (docSnap.id !== dateKey) {
            const newRef = doc(db, "users", userId, "habitProgress", dateKey);
            batchPromises.push(setDoc(newRef, data, { merge: true }));

            batchPromises.push(deleteDoc(docSnap.ref));
          }
        }
      });

      if (batchPromises.length > 0) {
        await Promise.all(batchPromises);
        console.log("Data migration completed");
      }

      localStorage.setItem("progressDataMigrated", "true");
    } catch (error) {
      console.error("Migration error:", error);
    }
  }, [userId]);

  // ======================================================
  //  1) LOAD DASHBOARD TASKS
  // ======================================================
  useEffect(() => {
    if (!userId) return;

    const unsub = onSnapshot(
      collection(db, "users", userId, "dashboardTasks"),
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        tasksRefMap.current.dashboard = list;

        // Update combined tasks
        let combined = [];
        Object.values(tasksRefMap.current).forEach((arr) => {
          combined = [...combined, ...arr];
        });
        setTasks(combined);
      }
    );

    return () => unsub();
  }, [userId]);

  // ======================================================
  //  2) LOAD HABITS
  // ======================================================
  useEffect(() => {
    if (!userId) return;

    const unsub = onSnapshot(
      collection(db, "users", userId, "habits"),
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setHabits(list);
      }
    );

    return () => unsub();
  }, [userId]);

  // ======================================================
  //  3) LOAD REMINDERS
  // ======================================================
  useEffect(() => {
    if (!userId) return;

    const unsub = onSnapshot(
      collection(db, "users", userId, "reminders"),
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setReminders(list);
      }
    );

    return () => unsub();
  }, [userId]);

  // ======================================================
  //  4) TOGGLE TASK (Dashboard Tasks Only)
  // ======================================================
  const toggleTask = async (taskId) => {
    if (!userId) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const todayKey = format(new Date(), "yyyy-MM-dd");

    const ref = doc(db, "users", userId, "dashboardTasks", taskId);

    try {
      await updateDoc(ref, {
        [`completedDates.${todayKey}`]: !task.completedDates?.[todayKey],
      });

      // Update progress after toggling task
      updateDailyProgress();
    } catch (error) {
      console.error("Error toggling task:", error);
    }
  };

  // ======================================================
  //  5) TOGGLE HABIT
  // ======================================================
  const toggleHabit = async (habitId) => {
    if (!userId) return;

    const habit = habits.find((h) => h.id === habitId);
    if (!habit) return;

    const todayKey = format(new Date(), "yyyy-MM-dd");

    const ref = doc(db, "users", userId, "habits", habitId);

    try {
      await updateDoc(ref, {
        [`completedDates.${todayKey}`]: !habit.completedDates?.[todayKey],
      });

      // Update progress after toggling habit
      updateDailyProgress();
    } catch (error) {
      console.error("Error toggling habit:", error);
    }
  };

  // ======================================================
  //  6) ADD TASK
  // ======================================================
  const addTask = async (taskData) => {
    if (!userId) return;

    try {
      await addDoc(collection(db, "users", userId, "dashboardTasks"), {
        text: taskData.text || "",
        date: taskData.date || null,
        recurring: taskData.recurring || false,
        sticky: taskData.sticky || false,
        completedDates: {},
        createdAt: new Date(),
      });

      // Update progress after adding task
      setTimeout(() => {
        updateDailyProgress();
      }, 100);
    } catch (error) {
      console.error("Error adding task:", error);
    }
  };

  // Update progress whenever tasks or habits change
  useEffect(() => {
    if (userId && (tasks.length > 0 || habits.length > 0)) {
      updateDailyProgress();
    }
  }, [tasks, habits, userId, updateDailyProgress]);

  // Run migration once on mount
  useEffect(() => {
    if (userId) {
      migrateProgressData();
    }
  }, [userId, migrateProgressData]);

  // ======================================================
  //  PROVIDER VALUE
  // ======================================================
  return (
    <DashboardContext.Provider
      value={{
        tasks,
        habits,
        reminders,
        addTask,
        toggleTask,
        toggleHabit,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}
